import { ArrowRight, Brush, Plus } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { cn } from 'src/shared/components/ui/utils'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { ChangeDecorationMenuItems } from 'src/pages/_myAccount/EditDecoration/components/ChangeDecorationDialog'

export const DecorationsMetaControl = ({
  quote_item,
  isReadOnly,
  onAddDecoration,
  onEditDecoration,
  onChangeDecoration,
  t,
}: {
  quote_item: QuoteItem_
  isReadOnly: boolean
  onAddDecoration: () => void
  onEditDecoration: (subItemId: number) => void
  onChangeDecoration: (subItemId: number) => void
  t: (en: string, sv?: string) => string
}) => {
  const decorations =
    quote_item.quote_sub_item?.filter((qsi) => qsi.type === 'decoration') ?? []
  const decorationCount = decorations.length

  const decorationText =
    decorationCount > 0
      ? `${decorationCount} ${
          decorationCount === 1
            ? t('decoration', 'dekoration')
            : t('decorations', 'dekorationer')
        }`
      : t('Add decoration', 'Lägg till dekoration')

  if (decorationCount === 0) {
    if (isReadOnly) {
      return (
        <Row className="items-center gap-1.5">
          <Brush className="h-3 w-3 shrink-0 text-gray-500" />
          <span className="whitespace-nowrap text-xs text-gray-600">
            {t('No decorations', 'Inga dekorationer')}
          </span>
        </Row>
      )
    }

    return (
      <button
        type="button"
        onClick={onAddDecoration}
        className={cn(
          'flex w-fit items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900',
          'heartbeat-border-3'
        )}
      >
        <Plus className="h-3 w-3 shrink-0 text-gray-500" />
        <span className="whitespace-nowrap text-xs">{decorationText}</span>
      </button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-fit items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <Brush className="h-3 w-3 shrink-0 text-gray-500" />
          <span className="whitespace-nowrap text-xs">{decorationText}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="start">
        {decorations.map((decoration, index) => (
          <Column key={decoration.id}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="truncate py-1 text-xs font-medium text-muted-foreground">
              {t(decoration.description || t('Decoration', 'Dekoration'))}
            </DropdownMenuLabel>
            {isReadOnly ? (
              <DropdownMenuItem disabled className="gap-2">
                <ArrowRight className="h-3.5 w-3.5" />
                {t('View decoration', 'Visa dekoration')}
              </DropdownMenuItem>
            ) : (
              <ChangeDecorationMenuItems
                onManualDesign={() => onEditDecoration(decoration.id)}
                onChangeDecoration={() => onChangeDecoration(decoration.id)}
              />
            )}
          </Column>
        ))}
        {!isReadOnly ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2" onClick={onAddDecoration}>
              <Plus className="h-3.5 w-3.5" />
              {t('Add decoration', 'Lägg till dekoration')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
